'use client';

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { fetchApi } from '@/lib/api/client';

interface MeResponse {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  avatarUrl: string | null;
  company: { id: string; name: string; slug: string };
  workspaces: Array<{ id: string; name: string; slug: string; role: string }>;
}

/**
 * Hydrates the Zustand auth store from /auth/me when there's an access
 * token in localStorage but no user in state. Handles edge cases where
 * login happens outside the normal useAuthStore.login() flow:
 *   - SSO redirects that set tokens server-side
 *   - Playwright / test logins that write tokens directly to localStorage
 *   - Tokens persisted from a previous session after a hard reload
 *
 * Mounted once at the root layout (inside QueryProvider). Idempotent —
 * runs only when a real-session token exists AND user is null AND not
 * already in-flight.
 */
export function AuthHydrationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const setWorkspace = useAuthStore((s) => s.setWorkspace);
  const setTokens = useAuthStore((s) => s.setTokens);
  const logout = useAuthStore((s) => s.logout);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Only hydrate when the store has a token but no user — covers the
    // case where the page refreshed mid-session or tokens were set outside
    // the normal login action.
    const token = localStorage.getItem('hf-app:access-token');
    if (!token || user || inFlightRef.current) return;

    inFlightRef.current = true;

    void (async () => {
      try {
        const me = await fetchApi<MeResponse>('/auth/me', {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          skipAuth: true,
        });

        setUser({
          id: me.id,
          email: me.email,
          firstName: me.firstName ?? '',
          lastName: me.lastName ?? '',
          role: me.role ?? '',
          companyId: me.company.id,
          companyName: me.company.name,
          avatarUrl: me.avatarUrl ?? null,
        });

        // Sync tokens into the store so setTokens keeps cookie in sync too
        const refreshToken = localStorage.getItem('hf-app:refresh-token') ?? '';
        if (refreshToken) {
          setTokens(token, refreshToken);
        }

        // Restore active workspace: prefer the persisted key; fall back to
        // the first workspace the user belongs to.
        const persistedWsId = localStorage.getItem('hf-app:workspace-id');
        const activeWsId =
          persistedWsId &&
          me.workspaces.some((w) => w.id === persistedWsId)
            ? persistedWsId
            : (me.workspaces[0]?.id ?? null);

        if (activeWsId) {
          setWorkspace(activeWsId);
        }
      } catch (err: unknown) {
        const status =
          err instanceof Error && 'statusCode' in err
            ? (err as { statusCode: number }).statusCode
            : (err as { status?: number }).status;
        if (status === 401) {
          // Token is expired or revoked — clear stale state so the next
          // navigation sends the user to /login.
          logout();
        }
        // For transient errors (network, 5xx) keep the token and retry on
        // the next mount cycle — do not log the user out.
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [user, setUser, setWorkspace, setTokens, logout]);

  return <>{children}</>;
}
